import { gql } from "@apollo/client";

/**
 * LEADFLOW MODULE - Centralized GraphQL Operations
 *
 * ALL GraphQL operations for the LeadFlow module live here.
 * Components import from this file — NEVER define inline gql.
 *
 * All types come from src/generated/graphql.ts
 */

// ==================== LEAD QUERIES ====================

/** Used by: LeadList.tsx, Dashboard.tsx */
export const GET_LEADS = gql`
  query GetLeads($stageId: String, $search: String, $archived: Boolean) {
    leads(stageId: $stageId, search: $search, archived: $archived) {
      id
      firstName
      lastName
      email
      phone
      source
      formId
      customFields
      stageId
      stageName
      tags
      convertedToClientId
      convertedAt
      notes
      isArchived
      createdAt
      updatedAt
    }
  }
`;

/** Used by: LeadDetail.tsx */
export const GET_LEAD = gql`
  query GetLead($id: ID!) {
    lead(id: $id) {
      id
      firstName
      lastName
      email
      phone
      source
      formId
      customFields
      stageId
      stageName
      tags
      convertedToClientId
      convertedAt
      notes
      ipAddress
      userAgent
      isArchived
      createdAt
      updatedAt
    }
  }
`;

/** Used by: Dashboard.tsx */
export const GET_LEAD_STATS = gql`
  query GetLeadStats {
    leadStats {
      total
      thisMonth
      thisWeek
      archived
      converted
      byStage
      customFieldTallies
    }
  }
`;

// ==================== LEAD MUTATIONS ====================

/** Used by: LeadDetail.tsx */
export const UPDATE_LEAD = gql`
  mutation UpdateLead($input: UpdateLeadInput!) {
    updateLead(input: $input) {
      id
      firstName
      lastName
      email
      phone
      stageId
      stageName
      tags
      notes
      updatedAt
    }
  }
`;

/** Used by: LeadDetail.tsx, LeadList.tsx */
export const ARCHIVE_LEAD = gql`
  mutation ArchiveLead($id: ID!) {
    archiveLead(id: $id) {
      id
      isArchived
    }
  }
`;

/** Used by: LeadDetail.tsx */
export const CONVERT_LEAD_TO_CLIENT = gql`
  mutation ConvertLeadToClient($id: ID!) {
    convertLeadToClient(id: $id) {
      id
      convertedToClientId
      convertedAt
    }
  }
`;

// ==================== PUBLIC FORM QUERIES (NO AUTH) ====================

/** Used by: PublicForm.tsx */
export const PUBLIC_LEAD_FLOW_FORM = gql`
  query PublicLeadFlowForm($slug: String!) {
    publicLeadFlowForm(slug: $slug) {
      id
      name
      slug
      displayStyle
      fields {
        name
        label
        type
        required
        placeholder
        options
        order
      }
      thankYouMessage
      isActive
    }
  }
`;

/** Used by: PublicForm.tsx */
export const SUBMIT_LEAD = gql`
  mutation SubmitLead($input: SubmitLeadInput!) {
    submitLead(input: $input) {
      id
      firstName
      email
      tokensAwarded
      tokenName
    }
  }
`;

// ==================== FORM QUERIES ====================

/** Used by: FormBuilder.tsx, Dashboard.tsx */
export const GET_FORMS = gql`
  query GetLeadFlowForms {
    leadFlowForms {
      id
      name
      slug
      displayStyle
      fields {
        name
        label
        type
        required
        placeholder
        options
        order
      }
      redirectUrl
      thankYouMessage
      notificationEmails
      isActive
      submissionCount
      stats {
        signupCount
        convertedCount
        customFieldTallies
      }
      createdAt
      updatedAt
    }
  }
`;

/** Used by: FormEmbed.tsx */
export const GET_FORM = gql`
  query GetLeadFlowForm($id: ID!) {
    leadFlowForm(id: $id) {
      id
      name
      slug
      displayStyle
      fields {
        name
        label
        type
        required
        placeholder
        options
        order
      }
      redirectUrl
      thankYouMessage
      notificationEmails
      isActive
      submissionCount
      createdAt
    }
  }
`;

// ==================== FORM MUTATIONS ====================

/** Used by: FormBuilder.tsx */
export const CREATE_FORM = gql`
  mutation CreateLeadFlowForm($input: CreateLeadFlowFormInput!) {
    createLeadFlowForm(input: $input) {
      id
      name
      slug
      displayStyle
      fields {
        name
        label
        type
        required
        placeholder
        options
        order
      }
      isActive
      createdAt
    }
  }
`;

/** Used by: FormBuilder.tsx */
export const UPDATE_FORM = gql`
  mutation UpdateLeadFlowForm($input: UpdateLeadFlowFormInput!) {
    updateLeadFlowForm(input: $input) {
      id
      name
      slug
      displayStyle
      fields {
        name
        label
        type
        required
        placeholder
        options
        order
      }
      redirectUrl
      thankYouMessage
      notificationEmails
      isActive
      updatedAt
    }
  }
`;

/** Used by: FormBuilder.tsx */
export const DELETE_FORM = gql`
  mutation DeleteLeadFlowForm($id: ID!) {
    deleteLeadFlowForm(id: $id)
  }
`;

// ==================== STAGE QUERIES ====================

/** Used by: StageManager.tsx, LeadList.tsx, LeadDetail.tsx */
export const GET_STAGES = gql`
  query GetLeadFlowStages {
    leadFlowStages {
      id
      name
      order
      color
      isDefault
      createdAt
    }
  }
`;

// ==================== STAGE MUTATIONS ====================

/** Used by: StageManager.tsx */
export const CREATE_STAGE = gql`
  mutation CreateLeadFlowStage($input: CreateLeadFlowStageInput!) {
    createLeadFlowStage(input: $input) {
      id
      name
      order
      color
      isDefault
    }
  }
`;

/** Used by: StageManager.tsx */
export const UPDATE_STAGE = gql`
  mutation UpdateLeadFlowStage($input: UpdateLeadFlowStageInput!) {
    updateLeadFlowStage(input: $input) {
      id
      name
      order
      color
      isDefault
    }
  }
`;

/** Used by: StageManager.tsx */
export const DELETE_STAGE = gql`
  mutation DeleteLeadFlowStage($id: ID!) {
    deleteLeadFlowStage(id: $id)
  }
`;

/** Used by: StageManager.tsx */
export const REORDER_STAGES = gql`
  mutation ReorderLeadFlowStages($input: ReorderLeadFlowStagesInput!) {
    reorderLeadFlowStages(input: $input)
  }
`;

// ==================== ACTION QUERIES ====================

/** Used by: ActionTemplates.tsx, LeadDetail.tsx */
export const GET_ACTIONS = gql`
  query GetLeadFlowActions {
    leadFlowActions {
      id
      name
      type
      subject
      body
      isHtml
      isActive
      createdAt
    }
  }
`;

/** Used by: LeadDetail.tsx */
export const GET_ACTION_LOGS = gql`
  query GetLeadFlowActionLogs($leadId: ID!) {
    leadFlowActionLogs(leadId: $leadId) {
      id
      leadId
      actionId
      channel
      status
      sentAt
      errorMessage
      subject
      body
      createdAt
    }
  }
`;

// ==================== ACTION MUTATIONS ====================

/** Used by: ActionTemplates.tsx */
export const CREATE_ACTION = gql`
  mutation CreateLeadFlowAction($input: CreateLeadFlowActionInput!) {
    createLeadFlowAction(input: $input) {
      id
      name
      type
      subject
      body
      isHtml
      isActive
    }
  }
`;

/** Used by: ActionTemplates.tsx */
export const UPDATE_ACTION = gql`
  mutation UpdateLeadFlowAction($input: UpdateLeadFlowActionInput!) {
    updateLeadFlowAction(input: $input) {
      id
      name
      type
      subject
      body
      isHtml
      isActive
    }
  }
`;

/** Used by: LeadDetail.tsx — now accepts optional subjectOverride/bodyOverride for AI-customised sends */
export const SEND_LEADFLOW_ACTION = gql`
  mutation SendLeadFlowAction($input: SendLeadFlowActionInput!) {
    sendLeadFlowAction(input: $input) {
      id
      leadId
      actionId
      channel
      status
      sentAt
      errorMessage
      subject
      body
    }
  }
`;

/** Used by: LeadDetail.tsx — AI customize preview, no DB writes */
export const CUSTOMIZE_LEADFLOW_ACTION = gql`
  mutation CustomizeLeadFlowAction($input: CustomizeLeadFlowActionInput!) {
    customizeLeadFlowAction(input: $input) {
      subject
      body
    }
  }
`;

/** Used by: LeadDetail.tsx — render an action template's merge fields against a lead, no DB writes, no AI.
 *  htmlBody is the full wrapped email (header + body + footer) using the LeadFlow per-module branding
 *  override layered over tenant.branding. Drop directly into a sandboxed iframe via srcDoc.
 */
export const PREVIEW_LEADFLOW_ACTION = gql`
  query PreviewLeadFlowAction($actionId: String!, $leadId: String!) {
    previewLeadFlowAction(actionId: $actionId, leadId: $leadId) {
      subject
      body
      htmlBody
      isHtml
    }
  }
`;

// ==================== EMAIL BRANDING (PER-MODULE OVERRIDE) ====================

const EMAIL_BRANDING_FIELDS = `
  id
  penName
  emailFooterText
  profilePhotoCid
  signatureImageCid
  bannerImageCid
  profilePhotoUrl
  signatureImageUrl
  bannerImageUrl
  socialLinks { linkedin instagram facebook twitter youtube website }
  ctaButtonText
  ctaButtonUrl
  ctaButtonStyle
  emailStyle
  emailFont
  primaryColor
  secondaryColor
  accentColor
  profilePhotoSize
  socialIconStyle
  hideTagline
  hideSiteName
  previewSampleBody
  createdAt
  updatedAt
`;

/** Used by: EmailBrandingSettings.tsx — load the singleton override for the tenant */
export const GET_LEADFLOW_EMAIL_BRANDING = gql`
  query GetLeadFlowEmailBranding {
    leadFlowEmailBranding {
      ${EMAIL_BRANDING_FIELDS}
    }
  }
`;

/** Used by: EmailBrandingSettings.tsx — upsert the singleton */
export const UPSERT_LEADFLOW_EMAIL_BRANDING = gql`
  mutation UpsertLeadFlowEmailBranding($input: LeadFlowEmailBrandingInput!) {
    upsertLeadFlowEmailBranding(input: $input) {
      ${EMAIL_BRANDING_FIELDS}
    }
  }
`;

/** Used by: EmailBrandingSettings.tsx — uploads profile / signature / banner image to IPFS */
export const UPLOAD_LEADFLOW_EMAIL_BRANDING_PHOTO = gql`
  mutation UploadLeadFlowEmailBrandingPhoto($field: BrandingPhotoField!, $file: Upload!) {
    uploadLeadFlowEmailBrandingPhoto(field: $field, file: $file) {
      ${EMAIL_BRANDING_FIELDS}
    }
  }
`;

/** Used by: EmailBrandingSettings.tsx — explicit $unset of one IPFS image slot.
 *  Cannot use the upsert mutation because Apollo strips undefined values, so
 *  the cleared field never reaches the backend.
 */
export const CLEAR_LEADFLOW_EMAIL_BRANDING_PHOTO = gql`
  mutation ClearLeadFlowEmailBrandingPhoto($field: BrandingPhotoField!) {
    clearLeadFlowEmailBrandingPhoto(field: $field) {
      ${EMAIL_BRANDING_FIELDS}
    }
  }
`;

/** Used by: EmailBrandingSettings.tsx — server-rendered live preview for the iframe */
export const PREVIEW_LEADFLOW_EMAIL_BRANDING = gql`
  query PreviewLeadFlowEmailBranding($input: LeadFlowEmailBrandingInput!, $sampleBody: String) {
    previewLeadFlowEmailBranding(input: $input, sampleBody: $sampleBody)
  }
`;

/** Used by: EmailBrandingSettings.tsx — fetch tenant defaults so blank-field placeholders show what falls through */
export const GET_TENANT_BRANDING_DEFAULTS = gql`
  query GetTenantBrandingDefaults {
    currentTenant {
      id
      name
      branding {
        siteName
        penName
        emailFooterText
        emailStyle
        emailFont
        primaryColor
        secondaryColor
        accentColor
        ctaButtonText
        ctaButtonUrl
        ctaButtonStyle
        profilePhoto
        profilePhotoSize
        socialIconStyle
        socialLinks { linkedin instagram facebook twitter youtube website }
      }
    }
  }
`;

/** Used by: EmailBrandingSettings.tsx — sends the live preview HTML to the logged-in user's own email */
export const SEND_TEST_LEADFLOW_EMAIL = gql`
  mutation SendTestLeadFlowEmail($html: String!, $subject: String) {
    sendTestLeadFlowEmail(html: $html, subject: $subject)
  }
`;

// ==================== NOTIFICATION RULE QUERIES ====================

/** Used by: NotificationSettings.tsx */
export const GET_NOTIFICATION_RULES = gql`
  query GetLeadFlowNotificationRules {
    leadFlowNotificationRules {
      id
      name
      trigger
      recipientType
      recipientIds
      recipientRole
      channels
      timing
      timingMinutes
      isEnabled
      messageTemplate
      createdAt
    }
  }
`;

// ==================== NOTIFICATION RULE MUTATIONS ====================

/** Used by: NotificationSettings.tsx */
export const CREATE_NOTIFICATION_RULE = gql`
  mutation CreateLeadFlowNotificationRule($input: CreateLeadFlowNotificationRuleInput!) {
    createLeadFlowNotificationRule(input: $input) {
      id
      name
      trigger
      recipientType
      channels
      isEnabled
    }
  }
`;

/** Used by: NotificationSettings.tsx */
export const UPDATE_NOTIFICATION_RULE = gql`
  mutation UpdateLeadFlowNotificationRule($input: UpdateLeadFlowNotificationRuleInput!) {
    updateLeadFlowNotificationRule(input: $input) {
      id
      name
      trigger
      recipientType
      channels
      isEnabled
      messageTemplate
    }
  }
`;

/** Used by: NotificationSettings.tsx */
export const DELETE_NOTIFICATION_RULE = gql`
  mutation DeleteLeadFlowNotificationRule($id: ID!) {
    deleteLeadFlowNotificationRule(id: $id)
  }
`;

/** Used by: NotificationSettings.tsx */
export const TOGGLE_NOTIFICATION_RULE = gql`
  mutation ToggleLeadFlowNotificationRule($id: ID!) {
    toggleLeadFlowNotificationRule(id: $id) {
      id
      isEnabled
    }
  }
`;

// ==================== DOMAIN QUERIES ====================

/** Used by: DomainSettings.tsx */
export const GET_REGISTERED_DOMAINS = gql`
  query GetRegisteredDomains {
    registeredDomains {
      id
      domain
      corsAllowed
      verified
      verificationToken
      emailSendingEnabled
      createdAt
    }
  }
`;

// ==================== DOMAIN MUTATIONS ====================

/** Used by: DomainSettings.tsx */
export const REGISTER_DOMAIN = gql`
  mutation RegisterDomain($input: RegisterDomainInput!) {
    registerDomain(input: $input) {
      id
      domain
      corsAllowed
      verified
      verificationToken
    }
  }
`;

/** Used by: DomainSettings.tsx */
export const VERIFY_DOMAIN = gql`
  mutation VerifyDomain($input: VerifyDomainInput!) {
    verifyDomain(input: $input) {
      id
      domain
      verified
      emailSendingEnabled
    }
  }
`;

/** Used by: DomainSettings.tsx */
export const REMOVE_DOMAIN = gql`
  mutation RemoveDomain($id: ID!) {
    removeDomain(id: $id)
  }
`;

// ==================== SHARED QUERIES (from other modules) ====================

/** Used by: NotificationSettings.tsx (employee selection for recipients) */
export const GET_EMPLOYEES_FOR_NOTIFICATIONS = gql`
  query GetEmployeesForLeadFlowNotifications {
    employees {
      id
      workEmail
      client {
        id
        fName
        lName
      }
    }
  }
`;

/** Used by: NotificationSettings.tsx - client recipient selector */
export const GET_CLIENTS_FOR_NOTIFICATIONS = gql`
  query GetClientsForLeadFlowNotifications {
    clients {
      id
      fName
      lName
      email
      phoneNumber
    }
  }
`;

// ==================== PLEDGE CAMPAIGN QUERIES ====================

const PLEDGE_CAMPAIGN_FIELDS = `
  id
  tenantId
  name
  slug
  description
  productName
  tiers {
    minQuantity
    pricePerUnit
    label
    shippingCost
  }
  deadlineAt
  status
  currentUnlockedTierIndex
  isReferralEnabled
  coverImageUrl
  heroHeadline
  heroSubhead
  createdAt
  updatedAt
`;

/** Used by: PledgeCampaignList.tsx */
export const GET_PLEDGE_CAMPAIGNS = gql`
  query GetPledgeCampaigns {
    pledgeCampaigns {
      ${PLEDGE_CAMPAIGN_FIELDS}
    }
  }
`;

/** Used by: PledgeCampaignDetail.tsx, PledgeCampaignEdit.tsx */
export const GET_PLEDGE_CAMPAIGN = gql`
  query GetPledgeCampaign($id: ID!) {
    pledgeCampaign(id: $id) {
      ${PLEDGE_CAMPAIGN_FIELDS}
    }
  }
`;

/** Used by: PublicPledgePage.tsx — anonymous, tenantMiddleware only */
export const PUBLIC_PLEDGE_CAMPAIGN = gql`
  query PublicPledgeCampaign($slug: String!) {
    publicPledgeCampaign(slug: $slug) {
      ${PLEDGE_CAMPAIGN_FIELDS}
    }
  }
`;

/** Used by: PublicPledgePage.tsx — polled every 30s for live progress bar */
export const PUBLIC_PLEDGE_PROGRESS = gql`
  query PublicPledgeProgress($campaignSlug: String!) {
    publicPledgeProgress(campaignSlug: $campaignSlug) {
      currentPledgeCount
      currentTierIndex
      nextTierThreshold
      nextTierGap
      recentPledgerFirstNames
    }
  }
`;

/** Used by: PublicPledgePage.tsx — anonymous pledge submission */
export const SUBMIT_PLEDGE = gql`
  mutation SubmitPledge($input: SubmitPledgeInput!) {
    submitPledge(input: $input) {
      id
      campaignId
      leadId
      quantity
      referralCode
      referredByPledgeId
      notificationPreference
      status
      tokensAwarded
      tokenName
    }
  }
`;

export const CREATE_PLEDGE_CAMPAIGN = gql`
  mutation CreatePledgeCampaign($input: CreatePledgeCampaignInput!) {
    createPledgeCampaign(input: $input) {
      ${PLEDGE_CAMPAIGN_FIELDS}
    }
  }
`;

export const UPDATE_PLEDGE_CAMPAIGN = gql`
  mutation UpdatePledgeCampaign($input: UpdatePledgeCampaignInput!) {
    updatePledgeCampaign(input: $input) {
      ${PLEDGE_CAMPAIGN_FIELDS}
    }
  }
`;

export const DELETE_PLEDGE_CAMPAIGN = gql`
  mutation DeletePledgeCampaign($id: ID!) {
    deletePledgeCampaign(id: $id)
  }
`;

/** Used by: PledgeCampaignDetail.tsx — admin pledger table */
export const GET_PLEDGES_FOR_CAMPAIGN = gql`
  query GetPledgesForCampaign($campaignId: ID!) {
    pledgesForCampaign(campaignId: $campaignId) {
      id
      campaignId
      leadId
      quantity
      referralCode
      referredByPledgeId
      notificationPreference
      status
      invoiceBillId
      giftReason
      leadFirstName
      leadLastName
      leadEmail
      leadPhone
      createdAt
    }
  }
`;

/** Used by: PledgeCampaignDetail.tsx — "Issue pre-order invoices" button */
export const ISSUE_PREORDER_INVOICES = gql`
  mutation IssuePreorderInvoicesForCampaign($campaignId: ID!, $tierIndex: Int!) {
    issuePreorderInvoicesForCampaign(campaignId: $campaignId, tierIndex: $tierIndex) {
      invoicesCreated
      skipped
      errors
    }
  }
`;

/** Used by: LeadList.tsx bulk-action bar — sends one action template to N leads. */
export const SEND_LEADFLOW_ACTION_TO_MANY = gql`
  mutation SendLeadFlowActionToMany($input: SendLeadFlowActionToManyInput!) {
    sendLeadFlowActionToMany(input: $input) {
      sent
      failed
      errors
    }
  }
`;
